import type { RouteName } from '@jira/platform__routing-route-name-types/src/RouteName.tsx';
import type { RouteGroup } from '@jira/platform__routing-route-name-types/src/RouteGroup.tsx';
import { createRouterSelector } from '@atlassian/react-resource-router/src/controllers/router-store';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';

/*
 * Controls whether specific route groups can see the Rovo button.
 * Consult with the team in #global-rovo-button-roll-out in Slack if you have any questions.
 *
 * Add your routes groups here to control it using an experiment, feature gate, or static false value
 * Returning false will disable the Rovo button for the route group
 */
const blacklistedJiraRouteGroups: Partial<Record<RouteGroup, () => boolean>> = {
	polaris: () => false, // The Rovo Button is reused inside JPD app for internal control, so we need to disable the global Rovo Button for all Polaris routes to avoid conflicts
	servicedesk: () => expVal('jira_free_gen_editor', 'isEnabled', false), // JSM
	'project-settings-servicedesk': () => expVal('jira_free_gen_editor', 'isEnabled', false), // JSM project settings
	'customer-service': () => false, // customer service
	'helpcenter-settings': () => false, // CSM help center
};

/*
 * Controls whether specific routes can see the Rovo button.
 * Consult with the team in #global-rovo-button-roll-out in Slack if you have any questions.
 *
 * Add your routes here to control it using an experiment, feature gate, or static false value
 * Returning false will disable the Rovo button for the route
 */
const blacklistedJiraRoutes: Partial<Record<RouteName, () => boolean>> = {
	// 2/3p experiences
	'connect-general': () => false,
	'forge-spa-global': () => false,
	// Planner
	'software-planner-plan': () => false,
	'software-planner-classic-plan': () => false,
	// JSM (additional routes not associated with servicedesk route group)
	'servicedesk-archived-work-items': () => expVal('jira_free_gen_editor', 'isEnabled', false),
	'jsm-list-nin': () => expVal('jira_free_gen_editor', 'isEnabled', false),
	'servicedesk-issues': () => expVal('jira_free_gen_editor', 'isEnabled', false),
	'forge-spa-servicedesk-queue': () => expVal('jira_free_gen_editor', 'isEnabled', false),
	'forge-spa-csm-queue': () => fg('csm_appification_standalone_navigation'),
	// CSM help center (additional routes not associated with helpcenter-settings route group)
	'helpcenter-settings-customer-access': () => false,
};

// oxlint-disable-next-line typescript/no-invalid-void-type
export const useIsGlobalRovoButtonAvailableForRoute: (args_0: void) => boolean =
	createRouterSelector((state) => {
		const routeName = state.route.name;
		const routeGroup = state.route.group;

		// Group-level blacklisting takes precedence
		if (routeGroup && blacklistedJiraRouteGroups[routeGroup]) {
			return blacklistedJiraRouteGroups[routeGroup]() ?? false;
		}

		// Exclude embedded routes
		if (routeName.endsWith('-embed')) {
			return false;
		}

		// Exclude individual blacklisted routes
		if (blacklistedJiraRoutes[routeName]) {
			return blacklistedJiraRoutes[routeName]() ?? false;
		}

		return true;
	});
